import React, { useMemo } from 'react';
import { Box, Divider, Paper, Tooltip, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import { CartesianGrid, LabelList, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { DEEP_GREEN, MUTED } from '../colorTokens';
import { GATE_COLOR, formatDays, formatNumber, formatPercent } from './format';

export const NUMERALS = { fontVariantNumeric: 'tabular-nums' };
const DIRECTION_COLOR = { better: GATE_COLOR.go, worse: GATE_COLOR.hold, flat: MUTED, neutral: MUTED };
const BETTER_NOTE = { lower: 'Lower is better.', higher: 'Higher is better.' };

export const versionRange = (versions) => (versions.length > 1 ? `${versions[0]} – ${versions[versions.length - 1]}` : versions[0]);

export const formatMeasure = (m, n) => {
  if (n == null) return '–';
  if (m.unit === 'rate') return formatPercent(n);
  if (m.unit === 'days') return formatDays(Math.round(n * 10) / 10);
  if (m.unit === 'score') return String(Math.round(n));
  return formatNumber(Math.round(n));
};

const formatChange = (m) => {
  if (m.change == null) return null;
  const sign = m.change > 0 ? '+' : m.change < 0 ? '−' : '';
  if (m.unit === 'rate') return `${sign}${Math.abs(m.change * 100).toFixed(1)} pts`;
  if (m.unit === 'score') return `${sign}${Math.abs(m.change).toFixed(0)} pts`;
  return `${sign}${Math.abs(m.change * 100).toFixed(0)}%`;
};

export const Delta = ({ measure, large }) => {
  const text = formatChange(measure);
  if (!text) return null;
  const Icon = measure.direction === 'flat' ? RemoveIcon : measure.change > 0 ? ArrowUpwardIcon : ArrowDownwardIcon;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: DIRECTION_COLOR[measure.direction], fontWeight: 700, fontSize: large ? '1.125rem' : '0.875rem', ...NUMERALS }}>
      <Icon sx={{ fontSize: large ? 20 : 16 }} />
      {text}
    </Box>
  );
};

/** One number with its change against the earlier value. baselineLabel names what "earlier" is, for the tooltip. */
export const Measure = ({ measure, baselineLabel }) => {
  const note = BETTER_NOTE[measure.better] || 'Shown for context; this is not a quality measure.';
  return (
    <Tooltip title={baselineLabel && measure.earlier != null ? `Earlier: ${baselineLabel}. ${note}` : ''} placement="top" disableHoverListener={!baselineLabel}>
      <Box sx={{ minWidth: 150 }}>
        <Typography variant="caption" sx={{ color: MUTED }}>{measure.label}</Typography>
        <Typography sx={{ fontSize: '2rem', fontWeight: 600, color: DEEP_GREEN, lineHeight: 1.15, ...NUMERALS }}>{formatMeasure(measure, measure.value)}</Typography>
        {measure.earlier != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Delta measure={measure} />
            <Typography variant="caption" sx={{ color: MUTED, ...NUMERALS }}>was {formatMeasure(measure, measure.earlier)}</Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export const MeasureRow = ({ measures, baselineLabel, skip = [] }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 3, md: 6 } }}>
    {measures.filter((m) => !skip.includes(m.key)).map((m) => <Measure key={m.key} measure={m} baselineLabel={baselineLabel} />)}
  </Box>
);

/** Large headline numeral. value is already formatted; since is the sentence under the delta. */
export const Headline = ({ caption, value, label, measure, since }) => (
  <Box>
    <Typography variant="caption" sx={{ color: MUTED }}>{caption}</Typography>
    <Typography sx={{ fontSize: '4.25rem', fontWeight: 300, lineHeight: 1.05, color: DEEP_GREEN, letterSpacing: '-0.02em', ...NUMERALS }}>{value}</Typography>
    <Typography sx={{ color: MUTED }}>{label}</Typography>
    {measure && measure.change != null && (
      <Box sx={{ mt: 1.5 }}>
        <Delta measure={measure} large />
        {since && <Typography variant="body2" sx={{ color: MUTED, mt: 0.25 }}>{since}</Typography>}
      </Box>
    )}
  </Box>
);

const niceTicks = (values, unit, axis) => {
  const scale = unit === 'score' ? 100 : 1;
  const real = values.filter((v) => v != null).map((v) => v / scale);
  if (axis === 'zero') {
    const max = real.length ? Math.max(...real) : 0.05;
    const step = [0.005, 0.01, 0.02, 0.05, 0.1, 0.2].find((s) => (max * 1.15) / s <= 4) || 0.2;
    const top = Math.max(step, Math.ceil((max * 1.15) / step) * step);
    return Array.from({ length: Math.round(top / step) + 1 }, (_, i) => Math.round(i * step * 1000) / 1000 * scale);
  }
  const lowest = Math.max(0, Math.floor((Math.min(...real, 1) - 0.05) * 10));
  return Array.from({ length: 11 - lowest }, (_, i) => ((lowest + i) / 10) * scale);
};

const TrendDot = ({ cx, cy, payload }) => {
  if (cx == null || cy == null) return null;
  const feature = payload.kind === 'feature';
  return <circle cx={cx} cy={cy} r={feature ? 6 : 4} fill={feature ? '#fff' : DEEP_GREEN} stroke={DEEP_GREEN} strokeWidth={feature ? 2.5 : 0} style={{ cursor: 'pointer' }} />;
};

/**
 * Line of one measure across releases. axis 'top' suits a number that should sit near its ceiling (pass rate, score),
 * axis 'zero' one that should sit near zero (failure rate). tip(point) returns { title, kind, lines }.
 */
export const TrendLine = ({ title, legend, data, valueKey, unit, axis = 'top', xKey = 'version', tip, onSelect, height = 230 }) => {
  const ticks = useMemo(() => niceTicks(data.map((d) => d[valueKey]), unit, axis), [data, valueKey, unit, axis]);
  const fmt = (v) => (unit === 'score' ? String(Math.round(v)) : `${(v * 100).toFixed(1)}%`);
  const tickFmt = (v) => (unit === 'score' ? String(v) : `${Math.round(v * 1000) / 10}%`);
  const onClick = (state) => {
    const hit = state && state.activePayload && state.activePayload[0];
    if (hit && onSelect) onSelect(hit.payload[xKey]);
  };
  const Tip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const t = tip(payload[0].payload);
    return (
      <Paper elevation={4} sx={{ p: 1.25, borderRadius: '10px' }}>
        <Typography sx={{ fontWeight: 700, color: DEEP_GREEN }}>{t.title} {t.kind && <Typography component="span" variant="caption" sx={{ color: MUTED }}>{t.kind}</Typography>}</Typography>
        {t.lines.map((l, i) => <Typography key={l} variant={i === 0 ? 'body2' : 'caption'} sx={i ? { color: MUTED, display: 'block' } : undefined}>{l}</Typography>)}
      </Paper>
    );
  };
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 600, color: DEEP_GREEN }}>{title}</Typography>
        {legend && <Typography variant="caption" sx={{ color: MUTED }}>{legend}</Typography>}
      </Box>
      <Box sx={{ height, mt: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 22, right: 24, left: 0, bottom: 0 }} onClick={onClick} style={{ cursor: onSelect ? 'pointer' : 'default' }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.08)" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: MUTED }} tickLine={false} axisLine={{ stroke: 'rgba(0,0,0,0.15)' }} interval={0} padding={{ left: 24, right: 24 }} />
            <YAxis domain={[ticks[0], ticks[ticks.length - 1]]} ticks={ticks} tickFormatter={tickFmt} tick={{ fontSize: 11, fill: MUTED }} tickLine={false} axisLine={false} width={44} />
            <ChartTooltip content={<Tip />} cursor={{ stroke: 'rgba(26,46,34,0.2)' }} />
            <Line type="monotone" dataKey={valueKey} stroke={DEEP_GREEN} strokeWidth={2.5} dot={<TrendDot />} activeDot={{ r: 7, fill: DEEP_GREEN }} isAnimationActive={false}>
              <LabelList dataKey={valueKey} position="top" offset={10} formatter={(v) => (v == null ? '' : fmt(v))} style={{ fontSize: 11, fontWeight: 600, fill: DEEP_GREEN }} />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

/** Headline numeral and chart side by side, the remaining measures underneath. */
export const ScoreCard = ({ headline, chart, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)', p: 3 }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: chart ? { xs: '1fr', md: 'minmax(280px, 340px) 1fr' } : '1fr', gap: { xs: 3, md: 5 } }}>
      {headline}
      {chart}
    </Box>
    {children && (
      <>
        <Divider sx={{ my: 2.5 }} />
        {children}
      </>
    )}
  </Paper>
);

export const Caveat = ({ children }) => (
  <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1.5 }}>{children}</Typography>
);
